import { initializeApp } from "firebase/app";
import type { Analytics } from "firebase/analytics";
import { getConfig } from "../config";
import { getAuth } from "firebase/auth";

export type FirebaseEventFeatureName = `feature:manage_rounds` | 'feature:manage_workout_exercise_comment';

type StartedAnalytics = {
  analytics: Analytics;
  logEvent: typeof import("firebase/analytics").logEvent;
};

/**
 * List of automatically tracked events: https://support.google.com/analytics/table/13594742#query=
 */
export class FirebaseService {
  static instance: FirebaseService | null = null;
  private firebaseApp: ReturnType<typeof initializeApp>;
  private analyticsReady: Promise<StartedAnalytics | null> | null = null;

  private constructor() {
    const firebaseConfig = getConfig().firebase?.config;
    if (!firebaseConfig) {
      throw new Error("Firebase configuration is not provided.");
    }
    this.firebaseApp = initializeApp(firebaseConfig);
    // Sentry V2-8P / V2-8G: without a local measurement id the SDK depends on its
    // `webConfig` fetch alone, and when that fetch dies with the connection its init
    // promise rejects with no handler. A build without an id is a build whose Firebase
    // app has no Analytics property to send to, so it starts no Analytics — and does not
    // download the SDK either: it is loaded on demand, outside the first-paint bundle.
    // A chunk that fails to load only means no Analytics, never an error of its own.
    if (firebaseConfig.measurementId) {
      this.analyticsReady = import("firebase/analytics")
        .then(({ getAnalytics, logEvent }) => ({
          analytics: getAnalytics(this.firebaseApp),
          logEvent,
        }))
        .catch(() => null);
    }
    FirebaseService.instance = this;
  }

  getFirebaseApp() {
    return this.firebaseApp;
  }

  getFirebaseAuth() {
    if (!this.firebaseApp) {
      throw new Error("Firebase App is not initialized.");
    }
    return getAuth(this.firebaseApp);
  }

  static getInstance() {
    if (!FirebaseService.instance) {
      FirebaseService.instance = new FirebaseService();
    }
    return FirebaseService.instance;
  }

  logEvent(eventName: string, eventParams?: Record<string, any>): void;
  logEvent(eventName: FirebaseEventFeatureName): void;
  logEvent(eventName: string | FirebaseEventFeatureName, eventParams?: Record<string, any>): void {
    void this.analyticsReady?.then((started) => {
      started?.logEvent(started.analytics, eventName, eventParams);
    });
  }
}
